import axios from "../axios";

const isDev = process.env.NODE_ENV === "development";
const logServiceDebug = (...args) => {
  if (isDev && typeof console !== "undefined" && console.debug) {
    // eslint-disable-next-line no-console
    console.debug("[complianceService]", ...args);
  }
};

const omitNullish = (params = {}) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {});

const buildAnalysisParams = ({
  scope = "company",
  scopeId,
  page = 1,
  limit = 20,
  sort = "rank:desc",
  state,
  search,
} = {}) => {
  const params = omitNullish({ scope, scopeId, page, limit, sort, search });
  if (state && state !== "all") {
    params["filter[state]"] = state;
  }
  return params;
};

const buildTaskParams = ({
  orgAdminId,
  entityType,
  page = 1,
  limit = 20,
  sort = "rank:desc",
} = {}) => {
  const params = omitNullish({ orgAdminId, page, limit, sort });
  if (entityType && entityType !== "all") {
    params["filter[entityType]"] = entityType;
  }
  return params;
};

/**
 * Compliance Service
 * Handles all API calls for the annual compliance dashboard
 */

// ============================================================================
// BOOTSTRAP & DATA FETCHING
// ============================================================================

/**
 * Fetch all compliance data for the current user (Super Admin or Org Admin)
 * Returns corporates, companies, templates, assignments, etc.
 */
const fetchComplianceData = (params) => {
  logServiceDebug("fetchComplianceData", params);
  return axios.get("/compliance", {
    params,
  });
};

/**
 * Fetch compliance data for a specific corporate
 */
const getCorporateCompliance = (corporateId, params) =>
  axios.get(`/compliance/corporates/${corporateId}`, { params });

/**
 * Fetch compliance data for a specific company
 */
const getCompanyCompliance = (companyId, params) =>
  axios.get(`/compliance/companies/${companyId}`, { params });

// ============================================================================
// TEMPLATES
// ============================================================================

/**
 * List all compliance templates
 * By default, exclude ARCHIVED templates unless caller provides a status filter.
 */
const listTemplates = (params = {}) => {
  const hasStatusFilter =
    params && Object.prototype.hasOwnProperty.call(params, "status");
  const finalParams = hasStatusFilter
    ? params
    : { ...params, status: ["ACTIVE", "DRAFT"] };
  return axios.get("/compliance-templates", {
    params: finalParams,
  });
};

/**
 * Get a specific template by ID
 */
const getTemplate = (templateId) =>
  axios.get(`/compliance-templates/${templateId}`);

/**
 * Create a new compliance template
 */
const createTemplate = (payload) => {
  logServiceDebug("createTemplate", payload);
  return axios.post("/compliance-templates", payload);
};

/**
 * Update an existing template
 */
const updateTemplate = (templateId, payload) => {
  logServiceDebug("updateTemplate", { templateId, payload });
  return axios.patch(`/compliance-templates/${templateId}`, payload);
};

/**
 * Delete a template (archive)
 */
const deleteTemplate = (templateId) => {
  logServiceDebug("deleteTemplate", templateId);
  return axios.delete(`/compliance-templates/${templateId}`);
};

/**
 * Clone a template
 */
const cloneTemplate = (templateId, payload) => {
  logServiceDebug("cloneTemplate", { templateId, payload });
  return axios.post(`/compliance-templates/${templateId}/clone`, payload);
};

/**
 * Instantiate a template as a workflow for a company
 */
const instantiateTemplate = (templateId, payload) => {
  logServiceDebug("instantiateTemplate", { templateId, payload });
  return axios.post(`/compliance-templates/${templateId}/instantiate`, payload);
};

/**
 * Add a category to a template
 * Note: Categories are updated via PATCH on the template itself
 */
const addTemplateCategory = (templateId, payload) => {
  logServiceDebug("addTemplateCategory - use updateTemplate instead", {
    templateId,
    payload,
  });
  return axios.patch(`/compliance-templates/${templateId}`, {
    categories: payload.categories,
  });
};

/**
 * Update a category in a template
 * Note: Categories are updated via PATCH on the template itself
 */
const updateTemplateCategory = (templateId, categoryId, payload) => {
  logServiceDebug("updateTemplateCategory - use updateTemplate instead", {
    templateId,
    categoryId,
    payload,
  });
  // Categories are nested in template, need to update whole template
  return axios.patch(`/compliance-templates/${templateId}`, payload);
};

/**
 * Delete a category from a template
 * Note: Categories are updated via PATCH on the template itself
 */
const deleteTemplateCategory = (templateId, categoryId) => {
  logServiceDebug("deleteTemplateCategory - use updateTemplate instead", {
    templateId,
    categoryId,
  });
  // Categories are managed as part of template update
  return Promise.resolve({
    data: { status: true, message: "Use updateTemplate" },
  });
};

/**
 * Add a task to a template category
 * Note: Tasks are updated via PATCH on the template itself
 */
const addTemplateTask = (templateId, categoryId, payload) => {
  logServiceDebug("addTemplateTask - use updateTemplate instead", {
    templateId,
    categoryId,
    payload,
  });
  return Promise.resolve({
    data: { status: true, message: "Use updateTemplate" },
  });
};

/**
 * Update a task in a template category
 * Note: Tasks are updated via PATCH on the template itself
 */
const updateTemplateTask = (templateId, categoryId, taskId, payload) => {
  logServiceDebug("updateTemplateTask - use updateTemplate instead", {
    templateId,
    categoryId,
    taskId,
    payload,
  });
  return Promise.resolve({
    data: { status: true, message: "Use updateTemplate" },
  });
};

/**
 * Delete a task from a template category
 * Note: Tasks are updated via PATCH on the template itself
 */
const deleteTemplateTask = (templateId, categoryId, taskId) => {
  logServiceDebug("deleteTemplateTask - use updateTemplate instead", {
    templateId,
    categoryId,
    taskId,
  });
  return Promise.resolve({
    data: { status: true, message: "Use updateTemplate" },
  });
};

// ============================================================================
// TEMPLATE ASSIGNMENTS
// ============================================================================

/**
 * Assign a template to an org admin
 * @param {Object} payload - { templateId, orgAdminId, notes }
 */
const assignTemplateToOrgAdmin = (payload) => {
  logServiceDebug("assignTemplateToOrgAdmin", payload);
  return axios.post("/template-assignments/org-admin", payload);
};

/**
 * Assign a template to a corporate (alias for org admin assignment)
 */
const assignTemplateToCorporate = (corporateId, payload) => {
  logServiceDebug("assignTemplateToCorporate", { corporateId, payload });
  return axios.post("/template-assignments/org-admin", {
    ...payload,
    orgAdminId: corporateId,
  });
};

/**
 * Assign a template to a company
 */
const assignTemplateToCompany = (companyId, payload) => {
  logServiceDebug("assignTemplateToCompany", { companyId, payload });
  return axios.post("/template-assignments/company", {
    ...payload,
    companyId,
  });
};

/**
 * Get template assignments for an org admin
 */
const getOrgAdminTemplateAssignments = (corporateId, params = {}) => {
  logServiceDebug("getOrgAdminTemplateAssignments", { corporateId, params });
  return axios.get(`/template-assignments/org-admin/${corporateId}`, {
    params,
  });
};

/**
 * Get template assignments for a company
 */
const getCompanyTemplateAssignments = (companyId, params = {}) => {
  logServiceDebug("getCompanyTemplateAssignments", { companyId, params });
  return axios.get(`/template-assignments/company/${companyId}`, { params });
};

/**
 * Cascade template to all companies under org admin
 */
const cascadeTemplateToCompanies = (assignmentId, orgAdminCorporateId) => {
  logServiceDebug("cascadeTemplateToCompanies", {
    assignmentId,
    orgAdminCorporateId,
  });
  return axios.post(`/template-assignments/${assignmentId}/cascade`, {
    orgAdminCorporateId,
  });
};

/**
 * Revoke a template assignment
 */
const revokeTemplateAssignment = (assignmentId) => {
  logServiceDebug("revokeTemplateAssignment", assignmentId);
  return axios.delete(`/template-assignments/${assignmentId}`);
};

/**
 * Add a task to an assignment (enhancement)
 */
const addTaskToAssignment = (assignmentId, payload) => {
  logServiceDebug("addTaskToAssignment", { assignmentId, payload });
  return axios.post(`/template-assignments/${assignmentId}/tasks`, payload);
};

/**
 * Remove a task from an assignment
 */
const removeTaskFromAssignment = (assignmentId, taskId) => {
  logServiceDebug("removeTaskFromAssignment", { assignmentId, taskId });
  return axios.delete(`/template-assignments/${assignmentId}/tasks/${taskId}`);
};

/**
 * Get template assignment history
 */
const getTemplateHistory = (params) =>
  axios.get("/compliance/template-history", {
    params,
  });

// ============================================================================
// CATEGORIES & TASKS
// ============================================================================

/**
 * Add a category to a corporate
 */
const addCorporateCategory = (corporateId, payload) =>
  axios.post(`/compliance/corporates/${corporateId}/categories`, payload);

/**
 * Update a category in a corporate
 */
const updateCorporateCategory = (corporateId, categoryId, payload) =>
  axios.put(
    `/compliance/corporates/${corporateId}/categories/${categoryId}`,
    payload
  );

/**
 * Delete a category from a corporate
 */
const deleteCorporateCategory = (corporateId, categoryId) =>
  axios.delete(
    `/compliance/corporates/${corporateId}/categories/${categoryId}`
  );

/**
 * Add a task to a corporate category
 */
const addCorporateTask = (corporateId, categoryId, payload) =>
  axios.post(
    `/compliance/corporates/${corporateId}/categories/${categoryId}/tasks`,
    payload
  );

/**
 * Update a task in a corporate category
 */
const updateCorporateTask = (corporateId, categoryId, taskId, payload) =>
  axios.put(
    `/compliance/corporates/${corporateId}/categories/${categoryId}/tasks/${taskId}`,
    payload
  );

/**
 * Delete a task from a corporate category
 */
const deleteCorporateTask = (corporateId, categoryId, taskId) =>
  axios.delete(
    `/compliance/corporates/${corporateId}/categories/${categoryId}/tasks/${taskId}`
  );

/**
 * Add a category to a company
 */
const addCompanyCategory = (companyId, payload) =>
  axios.post(`/compliance/companies/${companyId}/categories`, payload);

/**
 * Update a category in a company
 */
const updateCompanyCategory = (companyId, categoryId, payload) =>
  axios.put(
    `/compliance/companies/${companyId}/categories/${categoryId}`,
    payload
  );

/**
 * Delete a category from a company
 */
const deleteCompanyCategory = (companyId, categoryId) =>
  axios.delete(`/compliance/companies/${companyId}/categories/${categoryId}`);

/**
 * Add a task to a company category
 */
const addCompanyTask = (companyId, categoryId, payload) =>
  axios.post(
    `/compliance/companies/${companyId}/categories/${categoryId}/tasks`,
    payload
  );

/**
 * Update a task in a company category
 */
const updateCompanyTask = (companyId, categoryId, taskId, payload) =>
  axios.put(
    `/compliance/companies/${companyId}/categories/${categoryId}/tasks/${taskId}`,
    payload
  );

/**
 * Delete a task from a company category
 */
const deleteCompanyTask = (companyId, categoryId, taskId) =>
  axios.delete(
    `/compliance/companies/${companyId}/categories/${categoryId}/tasks/${taskId}`
  );

/**
 * Bulk update tasks (for status changes, completions, etc.)
 */
const bulkUpdateTasks = (payload) =>
  axios.post("/compliance/tasks/bulk-update", payload);

// ============================================================================
// ORG ADMINS
// ============================================================================

/**
 * List organization admins
 */
const listOrgAdmins = (params) =>
  axios.get("/compliance/org-admins", {
    params,
  });

/**
 * Search organization admins by company name or registration number
 * Uses the dedicated compliance-org-admins endpoint
 * @param {Object} params - Search parameters
 * @param {string} params.search - Search term (company name or registration number)
 * @param {number} params.limit - Max results to return
 * @param {number} params.skip - Number of results to skip
 * @returns {Promise} - Promise resolving to { items, totalCount }
 */
const searchOrgAdmins = (params = {}) => {
  logServiceDebug("searchOrgAdmins", params);
  return axios.get("/compliance-org-admins/search", {
    params: omitNullish(params),
  });
};

/**
 * Get all organization admins (paginated)
 * Uses the dedicated compliance-org-admins endpoint
 */
const getAllOrgAdmins = (params = {}) => {
  logServiceDebug("getAllOrgAdmins", params);
  return axios.get("/compliance-org-admins", {
    params: omitNullish(params),
  });
};

/**
 * Get org admin by corporate ID
 */
const getOrgAdminByCorporate = (corporateId) => {
  logServiceDebug("getOrgAdminByCorporate", corporateId);
  return axios.get(`/compliance-org-admins/by-corporate/${corporateId}`);
};

/**
 * Get companies managed by an org admin
 * @param {string} corporateId - Org admin's corporate ID
 * @param {Object} params - Query params (limit, skip, status, search)
 */
const getOrgAdminCompanies = (corporateId, params = {}) => {
  logServiceDebug("getOrgAdminCompanies", { corporateId, params });
  return axios.get(`/compliance-org-admins/${corporateId}/companies`, {
    params: omitNullish(params),
  });
};

/**
 * Assign a company to an org admin
 * @param {string} corporateId - Org admin's corporate ID
 * @param {string} companyId - Company to assign
 */
const assignCompanyToOrgAdmin = (corporateId, companyId) => {
  logServiceDebug("assignCompanyToOrgAdmin", { corporateId, companyId });
  return axios.post(`/compliance-org-admins/${corporateId}/companies`, {
    companyId,
  });
};

/**
 * Get compliance statistics for an org admin
 * @param {string} corporateId - Org admin's corporate ID
 */
const getOrgAdminStats = (corporateId) => {
  logServiceDebug("getOrgAdminStats", corporateId);
  return axios.get(`/compliance-org-admins/${corporateId}/stats`);
};

/**
 * Get a specific org admin
 */
const getOrgAdmin = (orgAdminId) =>
  axios.get(`/compliance/org-admins/${orgAdminId}`);

/**
 * Create or update an organization admin
 */
const saveOrgAdmin = (payload) => axios.post("/compliance/org-admins", payload);

/**
 * Update an organization admin
 */
const updateOrgAdmin = (orgAdminId, payload) =>
  axios.put(`/compliance/org-admins/${orgAdminId}`, payload);

/**
 * Delete an organization admin
 */
const deleteOrgAdmin = (orgAdminId) =>
  axios.delete(`/compliance/org-admins/${orgAdminId}`);

// ============================================================================
// TASK ASSIGNMENTS & RESPONSIBILITIES
// ============================================================================

/**
 * Assign responsibility for a task
 */
const assignTaskResponsibility = (payload) =>
  axios.post("/compliance/tasks/assign-responsibility", payload);

/**
 * Get users available for assignment
 */
const getAssignableUsers = (entityId, entityType) =>
  axios.get("/compliance/users/assignable", {
    params: { entityId, entityType },
  });

// ============================================================================
// COMPLIANCE SUMMARY & ANALYTICS
// ============================================================================

/**
 * Fetch Markov analysis queue
 */
const fetchComplianceAnalysis = (options = {}) => {
  const params = buildAnalysisParams(options);
  logServiceDebug("fetchComplianceAnalysis", params);
  return axios.get("/compliance/analysis", { params });
};

/**
 * Fetch OrgAdmin compliance tasks
 */
const fetchOrgComplianceTasks = (options = {}) => {
  const params = buildTaskParams(options);
  logServiceDebug("fetchOrgComplianceTasks", params);
  return axios.get("/compliance/tasks", { params });
};

/**
 * Fetch tenant snapshot download URLs
 */
const fetchComplianceSnapshot = () => {
  logServiceDebug("fetchComplianceSnapshot");
  return axios.get("/compliance/analysis/tenant-snapshot");
};

/**
 * Trigger Markov recompute
 */
const recomputeComplianceAnalysis = () => {
  logServiceDebug("recomputeComplianceAnalysis");
  return axios.post("/compliance/analysis/recompute");
};

/**
 * Fetch compliance task settings
 */
const getComplianceTaskSettings = () => {
  logServiceDebug("getComplianceTaskSettings");
  return axios.get("/compliance/settings/task");
};

/**
 * Update compliance task settings
 */
const updateComplianceTaskSettings = (payload) => {
  logServiceDebug("updateComplianceTaskSettings", payload);
  return axios.put("/compliance/settings/task", payload);
};

// ============================================================================
// SANCTION LISTS
// ============================================================================

/**
 * List all sanction lists
 * @param {Object} params - Query params (page, limit)
 */
const listSanctionLists = (params = {}) => {
  logServiceDebug("listSanctionLists", params);
  return axios.get("/sanction-lists", { params: omitNullish(params) });
};

/**
 * Get a single sanction list by ID
 * @param {string} sanctionListId - The sanction list ID
 */
const getSanctionList = (sanctionListId) => {
  logServiceDebug("getSanctionList", sanctionListId);
  return axios.get(`/sanction-lists/${sanctionListId}`);
};

/**
 * Create a new sanction list source
 * @param {Object} payload - { name, link }
 */
const createSanctionList = (payload) => {
  logServiceDebug("createSanctionList", payload);
  return axios.post("/sanction-lists", payload);
};

/**
 * Update a sanction list
 * @param {string} sanctionListId - The sanction list ID
 * @param {Object} payload - { name, link }
 */
const updateSanctionList = (sanctionListId, payload) => {
  logServiceDebug("updateSanctionList", { sanctionListId, payload });
  return axios.put(`/sanction-lists/${sanctionListId}`, payload);
};

/**
 * Edit sanction list content manually
 * @param {string} sanctionListId - The sanction list ID
 * @param {Object} payload - { content, workflow }
 */
const editSanctionContent = (sanctionListId, payload) => {
  logServiceDebug("editSanctionContent", { sanctionListId });
  return axios.put(`/sanction-lists/${sanctionListId}/content`, payload);
};

/**
 * Sync sanction list from its URL (fetch latest XML)
 * @param {string} sanctionListId - The sanction list ID
 */
const syncSanctionList = (sanctionListId) => {
  logServiceDebug("syncSanctionList", { sanctionListId });
  return axios.post(`/sanction-lists/${sanctionListId}/sync`);
};

/**
 * Get the latest version of a sanction list with download URL
 * @param {string} sanctionListId - The sanction list ID
 */
const getLatestSanctionVersion = (sanctionListId) => {
  logServiceDebug("getLatestSanctionVersion", sanctionListId);
  return axios.get(`/sanction-lists/${sanctionListId}/latest`);
};

/**
 * Verify sanction list sync status and S3 storage
 * @param {string} sanctionListId - The sanction list ID or URL
 * @param {Object} options - { verifyS3: boolean, byUrl: boolean }
 */
const verifySanctionList = (sanctionListId, options = {}) => {
  logServiceDebug("verifySanctionList", { sanctionListId, options });
  const params = new URLSearchParams();
  if (options.verifyS3 !== undefined) {
    params.append("verifyS3", options.verifyS3.toString());
  }
  if (options.byUrl !== undefined) {
    params.append("byUrl", options.byUrl.toString());
  }
  const queryString = params.toString();
  const url = `/sanction-lists/${sanctionListId}/verify${
    queryString ? `?${queryString}` : ""
  }`;
  return axios.get(url);
};

/**
 * Get sanction list cron job status
 */
const getSanctionListCronStatus = () => {
  logServiceDebug("getSanctionListCronStatus");
  return axios.get("/sanction-lists/cron/status");
};

/**
 * Update sanction list cron job configuration
 * @param {Object} config - { schedule: string, enabled: boolean }
 */
const updateSanctionListCronConfig = (config) => {
  logServiceDebug("updateSanctionListCronConfig", config);
  return axios.put("/sanction-lists/cron/config", config);
};

/**
 * Manually trigger the sanction list cron job
 */
const triggerSanctionListCron = () => {
  logServiceDebug("triggerSanctionListCron");
  return axios.post("/sanction-lists/cron/trigger");
};

/**
 * Fetch sanctions matches for an org admin
 * @param {string} orgAdminId - The org admin's user ID
 * @param {Object} params - Query params (page, limit, status)
 */
const getSanctionMatches = (orgAdminId, params = {}) => {
  logServiceDebug("getSanctionMatches", { orgAdminId, params });
  return axios.get(`/sanction-matches/org-admin/${orgAdminId}`, {
    params: omitNullish(params),
  });
};

/**
 * Update a sanction match workflow status
 * @param {string} matchId - The match workflow ID
 * @param {Object} payload - { status, resolution, notes }
 */
const updateSanctionMatch = (matchId, payload) => {
  logServiceDebug("updateSanctionMatch", { matchId, payload });
  return axios.put(`/sanction-matches/${matchId}`, payload);
};

// ============================================================================
// TASK RESPONSIBILITIES (Company-level)
// ============================================================================

/**
 * Get assignable users for a company's compliance tasks
 * @param {string} companyId - Company ID
 * @param {string} orgAdminId - Optional org admin ID
 */
const getCompanyAssignableUsers = (companyId, orgAdminId) => {
  logServiceDebug("getCompanyAssignableUsers", { companyId, orgAdminId });
  return axios.get(`/task-responsibilities/company/${companyId}/users`, {
    params: omitNullish({ orgAdminId }),
  });
};

/**
 * Get responsibilities for a company
 * @param {string} companyId - Company ID
 * @param {Object} params - Query params (page, limit, includeInactive)
 */
const getCompanyResponsibilities = (companyId, params = {}) => {
  logServiceDebug("getCompanyResponsibilities", { companyId, params });
  return axios.get(`/task-responsibilities/company/${companyId}`, {
    params: omitNullish(params),
  });
};

/**
 * Get responsibility for a specific task
 * @param {string} companyId - Company ID
 * @param {string} taskId - Task ID
 * @param {string} categoryId - Optional category ID
 */
const getTaskResponsibility = (companyId, taskId, categoryId) => {
  logServiceDebug("getTaskResponsibility", { companyId, taskId, categoryId });
  return axios.get(
    `/task-responsibilities/company/${companyId}/task/${taskId}`,
    {
      params: omitNullish({ categoryId }),
    }
  );
};

/**
 * Create a task responsibility assignment
 * @param {Object} payload - Assignment details
 */
const createTaskResponsibility = (payload) => {
  logServiceDebug("createTaskResponsibility", payload);
  return axios.post("/task-responsibilities", payload);
};

/**
 * Remove a responsibility assignment
 * @param {string} assignmentId - Assignment ID
 */
const removeTaskResponsibility = (assignmentId) => {
  logServiceDebug("removeTaskResponsibility", assignmentId);
  return axios.delete(`/task-responsibilities/${assignmentId}`);
};

/**
 * Bulk assign responsibilities from a template
 * @param {Object} payload - Bulk assignment details
 */
const bulkAssignResponsibilities = (payload) => {
  logServiceDebug("bulkAssignResponsibilities", payload);
  return axios.post("/task-responsibilities/bulk", payload);
};

// Legacy aliases for backward compatibility
const fetchSanctionsRecords = listSanctionLists;
const updateSanctionsRecord = updateSanctionList;
const uploadSanctionsXml = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  logServiceDebug("uploadSanctionsXml", file?.name);
  return axios.post("/sanction-lists/upload-xml", formData);
};

/**
 * Get aggregated compliance summary
 */
const getComplianceSummary = (params) =>
  axios.get("/compliance/summary", {
    params,
  });

/**
 * Get compliance metrics (legacy)
 */
const getComplianceMetrics = (params) =>
  axios.get("/compliance/metrics", {
    params,
  });

/**
 * Get global compliance metrics (Super Admin)
 */
const getGlobalMetrics = () => {
  logServiceDebug("getGlobalMetrics");
  return axios.get("/compliance/metrics/global");
};

/**
 * Get org admin compliance metrics
 * @param {string} corporateId - Org admin's corporate ID
 */
const getOrgAdminMetrics = (corporateId) => {
  logServiceDebug("getOrgAdminMetrics", corporateId);
  return axios.get(`/compliance/metrics/org-admin/${corporateId}`);
};

/**
 * Get company compliance metrics
 * @param {string} companyId - Company ID
 */
const getCompanyMetrics = (companyId) => {
  logServiceDebug("getCompanyMetrics", companyId);
  return axios.get(`/compliance/metrics/company/${companyId}`);
};

/**
 * Get compliance tasks (auto-generated by cron job)
 */
const getComplianceTasks = (params) =>
  axios.get("/compliance/tasks", {
    params,
  });

/**
 * Complete a compliance task
 */
const completeComplianceTask = (taskId, payload) =>
  axios.post(`/compliance/tasks/${taskId}/complete`, payload);

// ============================================================================
// COMPLIANCE TRACKER CONFIGURATION
// ============================================================================

/**
 * Get compliance tracker configuration (which tasks to display)
 */
const getTrackerConfig = (entityId, entityType) =>
  axios.get("/compliance/tracker/config", {
    params: { entityId, entityType },
  });

/**
 * Update compliance tracker configuration
 */
const updateTrackerConfig = (payload) =>
  axios.post("/compliance/tracker/config", payload);

// ============================================================================
// EXPORT & REPORTING
// ============================================================================

/**
 * Export compliance data
 */
const exportComplianceData = (params) =>
  axios.get("/compliance/export", {
    params,
    responseType: "blob",
  });

/**
 * Generate compliance report
 */
const generateComplianceReport = (payload) =>
  axios.post("/compliance/reports/generate", payload);

// ============================================================================
// SERVICE EXPORT
// ============================================================================

const complianceService = {
  // Bootstrap & Data
  fetchComplianceData,
  getCorporateCompliance,
  getCompanyCompliance,

  // Templates
  listTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  cloneTemplate,
  instantiateTemplate,
  addTemplateCategory,
  updateTemplateCategory,
  deleteTemplateCategory,
  addTemplateTask,
  updateTemplateTask,
  deleteTemplateTask,

  // Template Assignments
  assignTemplateToOrgAdmin,
  assignTemplateToCorporate,
  assignTemplateToCompany,
  getOrgAdminTemplateAssignments,
  getCompanyTemplateAssignments,
  cascadeTemplateToCompanies,
  revokeTemplateAssignment,
  addTaskToAssignment,
  removeTaskFromAssignment,
  getTemplateHistory,

  // Corporate Categories & Tasks
  addCorporateCategory,
  updateCorporateCategory,
  deleteCorporateCategory,
  addCorporateTask,
  updateCorporateTask,
  deleteCorporateTask,

  // Company Categories & Tasks
  addCompanyCategory,
  updateCompanyCategory,
  deleteCompanyCategory,
  addCompanyTask,
  updateCompanyTask,
  deleteCompanyTask,

  // Bulk Operations
  bulkUpdateTasks,

  // Org Admins
  listOrgAdmins,
  searchOrgAdmins,
  getAllOrgAdmins,
  getOrgAdminByCorporate,
  getOrgAdminCompanies,
  assignCompanyToOrgAdmin,
  getOrgAdminStats,
  getOrgAdmin,
  saveOrgAdmin,
  updateOrgAdmin,
  deleteOrgAdmin,

  // Assignments
  assignTaskResponsibility,
  getAssignableUsers,

  // Summary & Analytics
  getComplianceSummary,
  getComplianceMetrics,
  getGlobalMetrics,
  getOrgAdminMetrics,
  getCompanyMetrics,
  getComplianceTasks,
  completeComplianceTask,

  // Tracker Config
  getTrackerConfig,
  updateTrackerConfig,

  // Export & Reporting
  exportComplianceData,
  generateComplianceReport,
  // Compliance engine helpers
  fetchComplianceAnalysis,
  fetchOrgComplianceTasks,
  fetchComplianceSnapshot,
  recomputeComplianceAnalysis,
  getComplianceTaskSettings,
  updateComplianceTaskSettings,

  // Sanction Lists
  listSanctionLists,
  getSanctionList,
  createSanctionList,
  updateSanctionList,
  editSanctionContent,
  syncSanctionList,
  getLatestSanctionVersion,
  verifySanctionList,
  getSanctionListCronStatus,
  updateSanctionListCronConfig,
  triggerSanctionListCron,
  getSanctionMatches,
  updateSanctionMatch,

  // Legacy aliases
  fetchSanctionsRecords,
  updateSanctionsRecord,
  uploadSanctionsXml,

  // Task Responsibilities (Company-level)
  getCompanyAssignableUsers,
  getCompanyResponsibilities,
  getTaskResponsibility,
  createTaskResponsibility,
  removeTaskResponsibility,
  bulkAssignResponsibilities,
};

export default complianceService;
